import React, { useMemo } from 'react'
import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import {
  groupingRowColDef,
  TypedValueGetterParams,
  gridOptionsBase,
  AgGridWrapper,
  withAllContext,
  TypedRowNode,
  TypedCellRendererParams,
  filteredColumnDefsBuilder,
  StatusCellRenderer
} from 'view/components/datagrid'
import { Locale, Translation } from 'locale'
import { SelectButton } from 'view/components/options/optionsStyles'
import { TextButtonType } from 'view/components/buttons'
import { iconSet } from 'view/components/icons'
import { Collateral, Market } from 'api'
import { CollateralBasketProductsPanelGridRow } from 'view/shared/collateralBasket'
import { notNilFilter } from 'utils'
import { isNil } from 'lodash-es'
import { getCollateralStatusColor } from '../datagrid'

export enum CollateralBasketProductsGridColumnId {
  PRODUCT_NAME = 'PRODUCT_NAME',
  PRODUCT_SECTOR_NAME = 'PRODUCT_SECTOR_NAME',
  PRODUCT_COLLATERAL = 'PRODUCT_COLLATERAL',
  PRODUCT_COLLATERAL_ID = 'PRODUCT_COLLATERAL_ID',
  PRODUCT_COLLATERAL_SELECT = 'PRODUCT_COLLATERAL_SELECT'
}

export interface CollateralBasketProductsPanelGridProps {
  rowData: CollateralBasketProductsPanelGridRow[]
  disabled?: boolean
  removeCollateralId: (collateralIds: string[]) => void
  addCollateralId: (collateralIds: string[]) => void
  collateralBasketMarket?: Market
  showCollaterals: boolean
  viewOnly: boolean
}

export interface AddRemoveButtonRendererProps extends TypedCellRendererParams<CollateralBasketProductsPanelGridRow> {}

const createAddRemoveButton = (byCollateral: boolean) => {
  return class AddRemoveButton extends React.Component<AddRemoveButtonRendererProps> {
    render() {
      const { context, node } = this.props
      const data: CollateralBasketProductsPanelGridRow | undefined = node.group ? undefined : node.data
      const allLeafChildren = node.allLeafChildren ?? []

      const isSelected: boolean = node.group
        ? allLeafChildren.every((rowNode: TypedRowNode<CollateralBasketProductsPanelGridRow>) => rowNode.data?.selected)
        : !!data?.selected

      let collateralIds: string[] = []

      if (byCollateral) {
        collateralIds = (node.group
          ? allLeafChildren.map((rowNode: TypedRowNode<CollateralBasketProductsPanelGridRow>) => rowNode.data?.collateral?.id)
          : [node.id]
        ).filter(notNilFilter)
      } else {
        collateralIds = node.group
          ? allLeafChildren.flatMap((rowNode: TypedRowNode<CollateralBasketProductsPanelGridRow>) => {
              return rowNode.data?.collaterals?.map((collateral: Collateral) => collateral.id) ?? []
            })
          : node?.data?.collaterals?.map((collateral: Collateral) => collateral.id) ?? []
      }

      return (
        <SelectButton
          dataTestId={`CollateralBasketProductsGrid-SelectButton-${isSelected ? 'Tick' : Locale.translate(Translation.TC_BUTTON_ADD)}`}
          type={TextButtonType.Primary}
          selected={true}
          canFocus={false}
          onClick={() => !isNil(node.id) && context.updateCellValue(node.id, isSelected, 'selectButton', collateralIds)}>
          {isSelected ? iconSet.tickIcon : Locale.translate(Translation.TC_BUTTON_ADD)}
        </SelectButton>
      )
    }

    refresh = () => {
      this.forceUpdate()
      return true
    }
  }
}

const getBasketProductCollateralGridColumnDefs: (collateralBasketMarket?: Market) => (ColDef | ColGroupDef)[] = collateralBasketMarket => [
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    width: 150,
    minWidth: 140,
    showRowGroup: CollateralBasketProductsGridColumnId.PRODUCT_NAME,
    headerComponent: undefined,
    pinned: false,
    resizable: true,
    hide: false,
    sortable: false
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_NAME,
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.product?.name,
    rowGroup: true,
    hide: true,
    sortable: false
  },
  {
    showRowGroup: CollateralBasketProductsGridColumnId.PRODUCT_SECTOR_NAME,
    headerName: Locale.translate(Translation.TC_SECTOR),
    width: 150,
    pinned: false,
    resizable: false,
    sort: 'asc',
    hide: true
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_SECTOR_NAME,
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.productSector?.name,
    rowGroup: true,
    sort: 'asc',
    hide: true
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_COLLATERAL,
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.collateral?.name,
    headerName: Locale.translate(Translation.TC_COLLATERAL),
    suppressMovable: true,
    resizable: true,
    sort: 'asc',
    sortable: false,
    width: 170,
    minWidth: 170,
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: CollateralBasketProductsPanelGridRow) => data.collateral && getCollateralStatusColor(data.collateral.status),
      isBorderRadius: true
    }
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_COLLATERAL_ID,
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) =>
      collateralBasketMarket === Market.US ? params.data?.collateral?.cusip : params.data?.collateral?.isin,
    headerName: Locale.translate(collateralBasketMarket === Market.US ? Translation.TC_CUSIP : Translation.TC_ISIN),
    suppressMovable: true,
    resizable: true,
    sort: 'asc',
    sortable: false,
    width: 120,
    minWidth: 120
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_COLLATERAL_SELECT,
    suppressMovable: true,
    resizable: true,
    sortable: false,
    width: 70,
    equals: () => false,
    cellRenderer: withAllContext(createAddRemoveButton(true)),
    cellStyle: {
      'margin-top': '6px',
      'margin-left': '10px'
    }
  }
]

const basketProductsGridColumnDefs: (ColDef | ColGroupDef)[] = [
  {
    ...groupingRowColDef,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    showRowGroup: CollateralBasketProductsGridColumnId.PRODUCT_NAME,
    headerComponent: undefined,
    pinned: false,
    resizable: false,
    hide: false,
    sortable: false
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_NAME,
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.product?.name,
    rowGroup: true,
    hide: true,
    sortable: false,
    sort: 'asc'
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_SECTOR_NAME,
    valueGetter: (params: TypedValueGetterParams<CollateralBasketProductsPanelGridRow>) => params.data?.productSector?.name,
    headerName: Locale.translate(Translation.TC_SECTOR),
    pinned: false,
    resizable: false,
    sortable: false,
    sort: 'asc'
  },
  {
    colId: CollateralBasketProductsGridColumnId.PRODUCT_COLLATERAL_SELECT,
    suppressMovable: true,
    resizable: false,
    sortable: false,
    width: 70,
    equals: () => false,
    cellRenderer: withAllContext(createAddRemoveButton(false)),
    cellStyle: {
      'margin-top': '6px'
    }
  }
]

const gridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.rowId,
  groupDefaultExpanded: 0
}

export const CollateralBasketProductsGrid: React.FunctionComponent<CollateralBasketProductsPanelGridProps> = props => {
  const { rowData, collateralBasketMarket, showCollaterals, removeCollateralId, addCollateralId, viewOnly } = props

  const handleUpdate = (_rowId: string, isSelected: any, _updatedField: string, collateralIds: string) => {
    if (isSelected) {
      removeCollateralId((collateralIds as unknown) as string[])
    } else {
      addCollateralId((collateralIds as unknown) as string[])
    }
  }

  const filteredColumnDefs = useMemo(() => {
    const columnDefs = showCollaterals ? getBasketProductCollateralGridColumnDefs(collateralBasketMarket) : basketProductsGridColumnDefs
    return filteredColumnDefsBuilder(columnDefs)
      .withoutIf(viewOnly, [CollateralBasketProductsGridColumnId.PRODUCT_COLLATERAL_SELECT])
      .build()
  }, [collateralBasketMarket, showCollaterals, viewOnly])

  return (
    <AgGridWrapper
      className="datagrid-borderless"
      gridOptions={gridOptions}
      columnDefs={filteredColumnDefs}
      rowData={rowData}
      onCellValueUpdated={handleUpdate}
    />
  )
}
